import {
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import {
    DRAWER_WIDTH,
    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES,
    PARCELLAIRE_SMALL_SCREEN_MAX_HEIGHT,
    PARCELLAIRE_SMALL_SCREEN_MAX_WIDTH,
    PARCELLAIRE_TOOLBAR_BUTTON_STYLE,
    TRANSFORM_RANGE_ERROR,
    UNKNOWN_TRANSFORM_ERROR,
} from "./constants";
import {
    LEAFLET_OVERLAYED_BUTTONS_ZINDEX,
    MUI_DEFAULT_SIZES,
    ORTHOIMAGE_RGB_TGDT_UUID,
    PAGINATION,
} from "../../constants";
import { applyTransform, deleteLayers, readGeoJsonPlotFile } from "./utils";
import {
    formatDateISO,
    formatDateTime,
    formatSeconds,
    offset,
} from "../../utils/formatTime";
import { useCallback, useState } from "react";
import { useGetMapTiles, useLiteralSite } from "./hooks";

import { APPBAR_HEIGHT } from "../../layouts/orders/constants";
import AccountPopover from "../../layouts/orders/AccountPopover";
import { BACKEND_ROUTES } from "../../backendRoutes";
import ControlCameraIcon from "@mui/icons-material/ControlCamera";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import L from "leaflet";
import { LinearRuler } from "./LinearRuler";
import LockIcon from "@mui/icons-material/Lock";
import Logo from "../../components/Logo";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import { PARCELLAIRE_REDUCER_ACTIONS } from "./parcellaireReducer";
import PropTypes from "prop-types";
import { RadiometricTargetSaveModal } from "./RadiometricTargetSaveModal";
import SaveIcon from "@mui/icons-material/Save";
import { SoilSaveModal } from "./SoilSaveModal";
import { TileLayerControl } from "./TileLayerControl";
import { Timer } from "@mui/icons-material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import useSWR from "swr";
import { useSnackbar } from "../../hooks";

ParcellaireHeader.propTypes = {
    blueRectangleData: PropTypes.object,
    displayToggles: PropTypes.arrayOf(PropTypes.bool).isRequired,
    elapsedSeconds: PropTypes.number.isRequired,
    isLiteral: PropTypes.bool.isRequired,
    map: PropTypes.any,
    orthoimageName: PropTypes.string,
    openDrawer: PropTypes.bool.isRequired,
    parcellaireDispatch: PropTypes.func.isRequired,
    parcellaireState: PropTypes.object.isRequired,
    plotColor: PropTypes.string.isRequired,
    transformationToggles: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectableMetadata: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedTileLayer: PropTypes.object.isRequired,
    setBlueRectangleData: PropTypes.func.isRequired,
    setFirstToggles: PropTypes.func.isRequired,
    setGeoJsonKey: PropTypes.func.isRequired,
    setIsHCCSaveOpen: PropTypes.func.isRequired,
    setOpenExportForm: PropTypes.func.isRequired,
    setOpenOrthoForm: PropTypes.func.isRequired,
    setOrthoimageName: PropTypes.func.isRequired,
    setPlotColor: PropTypes.func.isRequired,
    setSecondToggles: PropTypes.func.isRequired,
    setSelectableMetadata: PropTypes.func.isRequired,
    setSelectedTileLayer: PropTypes.func.isRequired,
    soilPolygonsRef: PropTypes.object,
    radioMetricTargetPolygonRef: PropTypes.object,
    isRulerActive: PropTypes.bool.isRequired,
    setIsRulerActive: PropTypes.func.isRequired,
    searchParams: PropTypes.object.isRequired,
    setSearchParams: PropTypes.func.isRequired,
    missionGeometries: PropTypes.object,
    refetchMissionGeometries: PropTypes.func,
    orthoimageCompany: PropTypes.object,
    orthoimageContract: PropTypes.object,
    orthoimageSite: PropTypes.object,
    orthoimageMission: PropTypes.object,
};

export function ParcellaireHeader({
    blueRectangleData,
    displayToggles,
    elapsedSeconds,
    isLiteral,
    map,
    orthoimageName,
    openDrawer,
    parcellaireDispatch,
    parcellaireState,
    plotColor,
    transformationToggles,
    selectableMetadata,
    selectedTileLayer,
    setBlueRectangleData,
    setFirstToggles,
    setGeoJsonKey,
    setIsHCCSaveOpen,
    setOpenExportForm,
    setOpenOrthoForm,
    setOrthoimageName,
    setPlotColor,
    setSecondToggles,
    setSelectableMetadata,
    setSelectedTileLayer,
    soilPolygonsRef,
    radioMetricTargetPolygonRef,
    isRulerActive,
    setIsRulerActive,
    searchParams,
    setSearchParams,
    missionGeometries,
    refetchMissionGeometries,
    orthoimageCompany,
    orthoimageContract,
    orthoimageSite,
    orthoimageMission,
}) {
    const isSmallScreen = useMediaQuery(
        `(max-width:${PARCELLAIRE_SMALL_SCREEN_MAX_WIDTH}px),(max-height:${PARCELLAIRE_SMALL_SCREEN_MAX_HEIGHT}px)`
    );
    const { openSnackbar } = useSnackbar();
    const getMapTiles = useGetMapTiles(
        map,
        parcellaireState,
        selectedTileLayer
    );

    const [
        isRadioMetricTargetExportDialogOpen,
        setIsRadioMetricTargetExportDialogOpen,
    ] = useState(false);
    const [isSoilSaveModalOpen, setIsSoilSaveModalOpen] = useState(false);

    const siteUuid = searchParams.get("siteUuid");
    const missionUuid = searchParams.get("missionUuid");

    const { setValue } = useFormContext();

    const { data: site, error: siteError } = useLiteralSite();

    const { data: missions, error: missionsFetchError } = useSWR(
        siteUuid
            ? `${BACKEND_ROUTES.MISSION}?siteUuid=${siteUuid}&parentInfo=true&pipelineInstancesTGDTUuid=${ORTHOIMAGE_RGB_TGDT_UUID}&sort=date&limit=${PAGINATION.GENERIC.LIMIT.MAX}`
            : null
    );

    const handleFirstToggleGroupChange = useCallback(
        (_, values) => {
            try {
                setFirstToggles(values);
            } catch (error) {
                // TODO: error boundary https://hiphen.atlassian.net/browse/HCC-822
                openSnackbar(
                    error instanceof RangeError
                        ? TRANSFORM_RANGE_ERROR
                        : UNKNOWN_TRANSFORM_ERROR,
                    "error"
                );
            }
            setGeoJsonKey(new Date().getTime());
        },
        [openSnackbar, setFirstToggles, setGeoJsonKey]
    );

    const handleSecondToggleGroupChange = useCallback(
        (_, values) => {
            try {
                const transform = applyTransform(parcellaireState, values);
                setSecondToggles(values);
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.PM_EDIT,
                    transform,
                });
            } catch (error) {
                // TODO: error boundary https://hiphen.atlassian.net/browse/HCC-822
                openSnackbar(
                    error instanceof RangeError
                        ? TRANSFORM_RANGE_ERROR
                        : UNKNOWN_TRANSFORM_ERROR,
                    "error"
                );
            }
            setGeoJsonKey(new Date().getTime());
        },
        [
            openSnackbar,
            parcellaireDispatch,
            parcellaireState,
            setGeoJsonKey,
            setSecondToggles,
        ]
    );

    const toggleGroupOrientation = isSmallScreen ? "horizontal" : "vertical";

    const mergedFetchError = missionsFetchError ?? siteError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (isLiteral && !site) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="100vh"
            />
        );
    }

    const onGeoJsonUpload = (event) => {
        const geoJsonFile = event.target.files[0];

        if (geoJsonFile) {
            if (parcellaireState.initialGeoJsonData) {
                parcellaireDispatch({
                    type: PARCELLAIRE_REDUCER_ACTIONS.CLEAR_ALL_GEOJSON,
                });
            }

            readGeoJsonPlotFile(
                geoJsonFile,
                parcellaireDispatch,
                map,
                openSnackbar,
                setSelectableMetadata,
                site,
                isLiteral
            );

            // removing coord marker
            deleteLayers(map, (layer) => layer instanceof L.Marker);

            setBlueRectangleData(null);

            setValue("selectedMetadata", null);
        }
    };

    return (
        <Box
            sx={{ minHeight: APPBAR_HEIGHT, px: 3 }}
            display="flex"
            alignItems="center"
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: 1 }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        overflowX: "auto",
                        minWidth: 0,
                        py: 0.5,
                    }}
                >
                    <Logo
                        sx={{
                            height: MUI_DEFAULT_SIZES.BUTTON.MEDIUM,
                            width: (MUI_DEFAULT_SIZES.BUTTON.MEDIUM * 5) / 3,
                            flexShrink: 0,
                        }}
                    />

                    <Button
                        variant="contained"
                        component="label"
                        startIcon={<FileUploadIcon />}
                        sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                    >
                        Geojson plot file
                        <input
                            hidden
                            type="file"
                            accept=".geojson"
                            onChange={onGeoJsonUpload}
                        />
                    </Button>
                    {!isLiteral && (
                        <>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<FileUploadIcon />}
                                sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                                onClick={() => {
                                    setOpenOrthoForm(true);
                                }}
                            >
                                Orthoimage source
                            </Button>
                            {parcellaireState.metadata?.source === "select" && (
                                <Autocomplete
                                    id="mission"
                                    disabled={!missions}
                                    options={missions?.rows ?? []}
                                    value={
                                        missionUuid && missions?.rows
                                            ? missions.rows.find(
                                                  (mission) =>
                                                      mission.uuid ===
                                                      missionUuid
                                              )
                                            : null
                                    }
                                    sx={{
                                        ...PARCELLAIRE_TOOLBAR_BUTTON_STYLE,
                                        width: "200px",
                                    }}
                                    isOptionEqualToValue={(option, value) =>
                                        option.uuid === value.uuid
                                    }
                                    getOptionLabel={(option) =>
                                        formatDateISO(
                                            offset(new Date(option.date), true)
                                        )
                                    }
                                    onChange={(_, value) => {
                                        setSearchParams((prevState) => {
                                            const newSearchParams =
                                                new URLSearchParams(prevState);
                                            if (value) {
                                                newSearchParams.set(
                                                    "missionUuid",
                                                    value.uuid
                                                );
                                                newSearchParams.set(
                                                    "siteUuid",
                                                    value.siteUuid
                                                );
                                            } else {
                                                newSearchParams.delete(
                                                    "missionUuid"
                                                );
                                            }
                                            return newSearchParams;
                                        });
                                    }}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} key={option.uuid}>
                                                {formatDateTime(
                                                    offset(
                                                        new Date(option.date),
                                                        true
                                                    )
                                                )}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            name="mission"
                                            label="Orthoimage mission"
                                        />
                                    )}
                                />
                            )}
                            {parcellaireState.metadata && (
                                <TextField
                                    label="Name of the orthoimage"
                                    value={orthoimageName}
                                    sx={{ flexShrink: 0 }}
                                    onChange={(event) => {
                                        setOrthoimageName(event.target.value);
                                    }}
                                />
                            )}
                        </>
                    )}
                    <TextField
                        label="Plot color"
                        id="microplotColorPicker"
                        type="color"
                        sx={{
                            ...PARCELLAIRE_TOOLBAR_BUTTON_STYLE,
                            width: "120px",
                        }}
                        value={plotColor}
                        onChange={(e) => {
                            const newColor = e.target.value;
                            // Update styles directly on existing layers instead of
                            // re-rendering GeoJSON (avoids crash in rotation mode
                            // and is much more performant with rapid color changes)
                            map.eachLayer((layer) => {
                                if (
                                    layer.feature &&
                                    layer.setStyle &&
                                    layer.options.color === plotColor
                                ) {
                                    layer.setStyle({ color: newColor });
                                }
                            });
                            setPlotColor(newColor);
                        }}
                    />
                    {Boolean(selectableMetadata?.length) && (
                        <Controller
                            name="selectedMetadata"
                            render={({
                                field: { ref, onChange, name, ...fieldProps },
                            }) => (
                                <Autocomplete
                                    {...fieldProps}
                                    id="selectedMetadata"
                                    options={selectableMetadata}
                                    sx={{
                                        ...PARCELLAIRE_TOOLBAR_BUTTON_STYLE,
                                        width: "200px",
                                    }}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    getOptionLabel={(option) => option.label}
                                    onChange={(_, value) => {
                                        onChange(value);
                                        setGeoJsonKey(new Date().getTime());
                                    }}
                                    componentsProps={{
                                        popper: {
                                            style: { width: "fit-content" },
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={ref}
                                            name={name}
                                            fullWidth
                                            label="Displayed metadata"
                                        />
                                    )}
                                />
                            )}
                        />
                    )}
                    {isLiteral ? (
                        <Button
                            variant="contained"
                            component="label"
                            startIcon={<FileDownloadIcon />}
                            color="info"
                            sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                            onClick={() => {
                                setOpenExportForm(true);
                            }}
                            disabled={!parcellaireState.transformedGeoJson}
                        >
                            Export plot map
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            component="a"
                            startIcon={<SaveIcon />}
                            color="info"
                            sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                            disabled={!parcellaireState.transformedGeoJson}
                            onClick={() => {
                                setIsHCCSaveOpen(true);
                            }}
                        >
                            Save Plot Map
                        </Button>
                    )}
                    {!isLiteral && (
                        <>
                            <Button
                                variant="contained"
                                disabled={!soilPolygonsRef.current}
                                startIcon={<FileDownloadIcon />}
                                color="info"
                                sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                                onClick={() => setIsSoilSaveModalOpen(true)}
                            >
                                Save soil
                            </Button>
                            <Button
                                variant="contained"
                                disabled={!radioMetricTargetPolygonRef.current}
                                startIcon={<FileDownloadIcon />}
                                color="info"
                                sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                                onClick={() =>
                                    setIsRadioMetricTargetExportDialogOpen(true)
                                }
                            >
                                Save Radiometric Target
                            </Button>
                        </>
                    )}

                    {isLiteral && (
                        <Button
                            variant="contained"
                            component="label"
                            disabled={!parcellaireState.initialGeoJsonData}
                            startIcon={<FileDownloadIcon />}
                            color="info"
                            sx={PARCELLAIRE_TOOLBAR_BUTTON_STYLE}
                            onClick={getMapTiles}
                        >
                            Export map tiles
                        </Button>
                    )}
                </Box>
                {parcellaireState.transformedGeoJson && (
                    <Stack
                        direction={isSmallScreen ? "row" : "column"}
                        spacing={1}
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            left: 10,
                            zIndex: LEAFLET_OVERLAYED_BUTTONS_ZINDEX,
                        }}
                    >
                        <ToggleButtonGroup
                            color="secondary"
                            orientation={toggleGroupOrientation}
                            value={displayToggles}
                            onChange={handleFirstToggleGroupChange}
                            sx={{ bgcolor: "white" }}
                        >
                            {blueRectangleData && (
                                <ToggleButton
                                    value={
                                        GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.DISPLAY_BLUE_MODE
                                    }
                                    size="small"
                                    color="primary"
                                >
                                    <Tooltip
                                        placement="right"
                                        title={
                                            <Typography>
                                                Display field map
                                            </Typography>
                                        }
                                    >
                                        <CropSquareIcon />
                                    </Tooltip>
                                </ToggleButton>
                            )}
                            <ToggleButton
                                value={
                                    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.HIDDEN_PLOT_MODE
                                }
                                size="small"
                                color="primary"
                            >
                                <Tooltip
                                    placement="right"
                                    title={
                                        <Typography>Hide all plots</Typography>
                                    }
                                >
                                    <VisibilityOffIcon />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <ToggleButtonGroup
                            color="secondary"
                            orientation={toggleGroupOrientation}
                            value={transformationToggles}
                            onChange={handleSecondToggleGroupChange}
                            sx={{ bgcolor: "white" }}
                        >
                            <ToggleButton
                                value={
                                    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.SINGLE_TARGET_MODE
                                }
                                size="small"
                                color="primary"
                            >
                                <Tooltip
                                    placement="right"
                                    title={
                                        <>
                                            <Typography>
                                                Apply only single translations
                                            </Typography>
                                            <Typography>
                                                (Overrides other modes)
                                            </Typography>
                                        </>
                                    }
                                >
                                    <LooksOneIcon />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton
                                value={
                                    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.SHIFT_MODE
                                }
                                size="small"
                                color="primary"
                            >
                                <Tooltip
                                    placement="right"
                                    title={
                                        <Typography>
                                            Apply translation starting from the
                                            1st vector
                                        </Typography>
                                    }
                                >
                                    <ControlCameraIcon />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton
                                value={
                                    GEOMAN_CUSTOM_TOGGLE_BUTTON_NAMES.LOCKED_SHAPE_MODE
                                }
                                size="small"
                                color="primary"
                            >
                                <Tooltip
                                    placement="right"
                                    title={
                                        <Typography>
                                            Do not change plot shape
                                        </Typography>
                                    }
                                >
                                    <LockIcon />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                )}
                <Box
                    sx={(theme) => ({
                        backgroundColor: "silver",
                        position: "absolute",
                        p: 0.75,
                        borderRadius: 1,
                        bottom: 10,
                        transition: theme.transitions.create(
                            "right",
                            openDrawer
                                ? {
                                      duration:
                                          theme.transitions.duration
                                              .leavingScreen,
                                      easing: theme.transitions.easing.easeOut,
                                  }
                                : {
                                      duration:
                                          theme.transitions.duration
                                              .enteringScreen,
                                      easing: theme.transitions.easing.sharp,
                                  }
                        ),
                        right: openDrawer ? 10 + DRAWER_WIDTH : 10,
                        zIndex: "drawer",
                    })}
                >
                    <TileLayerControl
                        selectedTileLayer={selectedTileLayer}
                        setSelectedTileLayer={setSelectedTileLayer}
                    />
                </Box>
                <Box
                    sx={(theme) => ({
                        backgroundColor: "silver",
                        position: "absolute",
                        p: 0.75,
                        borderRadius: 1,
                        bottom: 10,
                        transition: theme.transitions.create(
                            "right",
                            openDrawer
                                ? {
                                      duration:
                                          theme.transitions.duration
                                              .leavingScreen,
                                      easing: theme.transitions.easing.easeOut,
                                  }
                                : {
                                      duration:
                                          theme.transitions.duration
                                              .enteringScreen,
                                      easing: theme.transitions.easing.sharp,
                                  }
                        ),
                        right: openDrawer ? 275 + DRAWER_WIDTH : 275,
                        zIndex: "drawer",
                    })}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <LinearRuler
                            map={map}
                            isActive={isRulerActive}
                            setIsActive={setIsRulerActive}
                        />
                        <Tooltip
                            title={
                                <span>
                                    Ctrl+A: Select / Unselect all
                                    <br />
                                    Ctrl+Q: Select (polygon)
                                    <br />
                                    Ctrl+R: Rotate
                                    <br />
                                    Ctrl+E: Scale
                                    <br />
                                    Ctrl+M: Ruler
                                </span>
                            }
                            placement="left"
                        >
                            <IconButton size="small">
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Time spent editing plot map">
                    <Chip
                        label={formatSeconds(elapsedSeconds)}
                        color="primary"
                        variant="outlined"
                        icon={<Timer />}
                        sx={{
                            visibility:
                                elapsedSeconds > 0 ? "visible" : "hidden",
                            fontWeight: "medium",
                            fontSize: "0.875rem",
                            "& .MuiChip-label": {
                                minWidth: "7ch",
                                fontVariantNumeric: "tabular-nums",
                                textAlign: "center",
                            },
                        }}
                    />
                </Tooltip>
                <AccountPopover />
            </Stack>
            <RadiometricTargetSaveModal
                open={isRadioMetricTargetExportDialogOpen}
                setIsOpen={setIsRadioMetricTargetExportDialogOpen}
                radioMetricTargetPolygonRef={radioMetricTargetPolygonRef}
                orthoimageCompany={orthoimageCompany}
                orthoimageContract={orthoimageContract}
                orthoimageSite={orthoimageSite}
                orthoimageMission={orthoimageMission}
                existingGeometry={missionGeometries?.radiometricTarget}
                refetchMissionGeometries={refetchMissionGeometries}
            />
            <SoilSaveModal
                open={isSoilSaveModalOpen}
                setIsOpen={setIsSoilSaveModalOpen}
                soilPolygonsRef={soilPolygonsRef}
                orthoimageCompany={orthoimageCompany}
                orthoimageContract={orthoimageContract}
                orthoimageSite={orthoimageSite}
                orthoimageMission={orthoimageMission}
                refetchMissionGeometries={refetchMissionGeometries}
            />
        </Box>
    );
}
